import { NgIf, NgStyle } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  forwardRef,
  input,
  OnInit,
  ViewEncapsulation
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'isg-click-filter',
  templateUrl: './click-filter.component.html',
  styleUrls: ['./click-filter.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ClickFilterComponent),
      multi: true
    }
  ],
  standalone: true,
  imports: [NgStyle, NgIf, FormsModule]
})
/**
 * Tri-state checkbox component that cycles through true, false, and null
 * states. Implements ControlValueAccessor for use with Angular forms.
 * Supports optional custom labels and a forced-width override.
 */
export class ClickFilterComponent implements ControlValueAccessor, OnInit {

  // ----------------------- Constants --------------------------------------

  /** Default label shown when the checked state is true. */
  protected readonly ctDefaultTextTrue = 'Active';

  /** Default label shown when the checked state is false. */
  protected readonly ctDefaultTextFalse = 'Inactive';

  /** Default label shown when the checked state is null. */
  protected readonly ctDefaultTextNotSpecified = '';

  /** Unit appended to the forced width value. */
  protected readonly ctWidthUnit = 'rem';

  /** Fallback width when no forced width is supplied. */
  protected readonly ctWidthAuto = 'auto';

  // ----------------------- Signal Inputs ----------------------------------

  /** Allows the null (indeterminate) state when true. */
  public nullable = input<boolean>(true);

  /** Label displayed when checked is true. */
  public textTrue = input<string>(this.ctDefaultTextTrue);

  /** Label displayed when checked is false. */
  public textFalse = input<string>(this.ctDefaultTextFalse);

  /** Label displayed when checked is null. */
  public textNotSpecified = input<string>(this.ctDefaultTextNotSpecified);

  /** Disables interaction when true. */
  public isDisabled = input<boolean>(false);

  /** Forces the component to a specific width in rem; null means auto. */
  public widthForced = input<number | null>(null);

  // ----------------------- Public Properties ------------------------------

  /** Current tri-state value: true, false, or null. */
  public checked?: boolean | null = false;

  // ----------------------- Constructor ------------------------------------

  constructor(protected cdr: ChangeDetectorRef) { }

  // ----------------------- Lifecycle Methods ------------------------------

  ngOnInit(): void { }

  // ----------------------- Event Handlers ---------------------------------

  /** Cycles the checked state and notifies the form control of the change. */
  onModelChange(e?: boolean): void {

    if (this.checked == null)
      this.checked = true;
    else if (this.checked == true)
      this.checked = false;
    else if (this.checked == false && this.nullable() == false)
      this.checked = true;
    else if (this.checked == false)
      this.checked = null;

    this.onChange(this.checked);

    this.cdr.detectChanges();
  }

  // ----------------------- UI Helper Methods ------------------------------

  /** Returns the CSS width string for the host element. */
  getWidth(): string {

    const forced = this.widthForced();

    if (forced && forced > 0)
      return forced.toString() + this.ctWidthUnit;

    return this.ctWidthAuto;
  }

  // ----------------------- ControlValueAccessor ---------------------------

  /** Registers the onChange callback provided by the forms API. */
  registerOnChange(fn: any): void {

    this.onChange = fn;
  }

  /** Registers the onTouched callback provided by the forms API. */
  registerOnTouched(fn: any): void {

    this.onTouch = fn;
  }

  /** Writes a new value to the component from the form control. */
  writeValue(checked: boolean): void {

    this.checked = checked;
  }

  // ----------------------- Forms API Callbacks ----------------------------

  /** Callback invoked when the value changes; set via registerOnChange. */
  onChange: any = () => { };

  /** Callback invoked when the control is touched; set via registerOnTouched. */
  onTouch: any = () => { };

}
